<template>
  <section class="related-persons-block" v-if="!state.persons || state.persons.length > 0">
    <header class="related-persons-block__header">
      <div class="related-persons-block__inner">
        <AppHeadingTitleSubtitle title="Gerelateerde" subtitle="personen" styling="subtitle"/>
      </div>
    </header>
    <div v-if="belongsToAll" class="related-persons-block__content">
      <swiper
          slides-per-view="auto"
          class="related-persons-block__slider"
          :watch-overflow="true"
          @swiper="useSwiper"
      >
        <swiper-slide
            v-for="(person) in belongsToAll"
            :key="`person-item_${person.id}`"
            class="related-persons-block__slider-slide"
        >
          <AppPersonCard :data="person"/>
        </swiper-slide>
        <AppSwiperControls />
      </swiper>
    </div>
  </section>
</template>

<script setup lang="ts">
import {Swiper, SwiperSlide} from 'swiper/vue'
import 'swiper/css'
import {TPersonPage} from '~/types/page-types'
import {computed, reactive} from 'vue'
import {getCollection} from "~/services/custom-endpoints";
import {useSwiper} from "~/composables/useSwiper";

const props = defineProps<{
  data: TPersonPage,
}>()

const state = reactive({
  persons: []
})

const pageCollection = 'person_page'
const fields = ['*', 'domain.*.*', 'belongs_to.*.*']

const filter = computed(() => {

  const or = props.data?.belongs_to.map((belongs_to) => {
    const collectionKey = 'item:' + belongs_to.collection
    const obj = {}
    if (belongs_to?.item) {
      obj[collectionKey] = {id: belongs_to.item.id}
    }
    return {...obj}
  })

  return {
    _and: [
      {
        belongs_to: {
          _or: or
        }
      },
      {id: {_neq: props.data?.id}},
      {status: 'published'}
    ]
  }
})

function shuffle(array) {
  let currentIndex = array.length;

  // While there remain elements to shuffle...
  while (currentIndex != 0) {

    // Pick a remaining element...
    let randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex], array[currentIndex]];
  }

  return array
}

const belongsToAll = computed(() => {

  // only return pages that came up in all queries (AND)
  if (state.persons.length !== 0 && props.data.belongs_to && props.data.belongs_to.length > 1) {

    const intersected = []

    state.persons.forEach((person) => {

      let matches = 0

      props.data.belongs_to.forEach((bt) => {

        const matched = !!person.belongs_to.find((pbt) => {
          return pbt.collection === bt.collection && pbt.item?.id === bt.item?.id
        })

        if (matched) {
          matches++
        }
      })

      if (matches === props.data.belongs_to.length) {
        intersected.push(person)
      }
    })

    return shuffle(intersected).slice(0, 18)

  } else {
    return shuffle([...state.persons]).slice(0, 18)
  }
})

onMounted(() => {
  window.requestAnimationFrame(async () => {
    if (props.data.belongs_to && props.data.belongs_to.length > 0) {
      const {data: result, pending, error} = await getCollection(
          pageCollection,
          filter.value,
          fields,
          ['-date_created'],
          50
      )

      if (result.value?.data) {
        state.persons = result.value?.data
      }
    }
  })

})

</script>

<style scoped lang="postcss">
.related-persons-block {
  position: relative;
  padding-bottom: 5rem;
  color: var(--color-blue-aqua);

  &__header {
    @mixin container;
    @mixin app-container-padding;
    @mixin app-grid;
    @mixin app-content-block-title-margins;
  }

  &__inner {
    grid-column: 1 / -1;
  }

  &__content {
    overflow: clip;
  }

  &__slider {
    overflow: visible;
  }

  &__slider-slide {
    position: relative;
    margin-right: 1.25rem;
    width: fit-content;

    @media (--md) {
      margin-right: 1.5rem;

      &:first-child {
        margin-left: 1rem;
      }
    }
  }
}
</style>
